import { styled } from '@mui/material/styles'
import TableRow from '@mui/material/TableRow'
import React from 'react'

type TableRowLayoutProps = {
  'data-testid'?: string
  isLoading?: boolean
  children: React.ReactNode
  className?: string
  onClick?: () => void
}

export default function TableRowLayout({
  isLoading,
  children,
  className,
  onClick,
  'data-testid': dataTestId,
}: TableRowLayoutProps) {
  const isInteractive = Boolean(onClick) && !isLoading

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (!isInteractive || !onClick || (event.key !== 'Enter' && event.key !== ' ')) {
      return
    }

    event.preventDefault()
    onClick()
  }

  return (
    <StyledRow
      className={className}
      data-testid={dataTestId}
      isInteractive={isInteractive}
      isLoading={isLoading}
      role={isInteractive ? 'link' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={handleKeyDown}
    >
      {children}
    </StyledRow>
  )
}

const StyledRow = styled(TableRow, {
  shouldForwardProp: (prop) => !['isInteractive', 'isLoading'].includes(prop as string),
})<{ isInteractive: boolean; isLoading?: boolean }>(({ isInteractive, isLoading, theme }) => ({
  ...(isInteractive
    ? {
        cursor: 'pointer',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }
    : {}),
  ...(isLoading
    ? {
        cursor: 'auto',
        opacity: '0.45',
        pointerEvents: 'none',
      }
    : {}),
}))
